import { DEFAULT_CUSTOM_RECIPE } from '../services/theme/customRecipe'

export type ThemeTokens = Record<string, string>

type ThemeMode = 'preset' | 'generated'
type ThemeGenerationBase = 'canvas-and-ink' | 'canvas-only'
export type BuiltInPaletteRecipeId = 'ink-wash' | 'tonal' | 'analogous' | 'split-complement' | 'triadic' | 'golden-angle'
export type PaletteRecipeId = BuiltInPaletteRecipeId | 'custom'
export type ThemeContrast = 'standard' | 'high'

export interface ThemeSettings {
  mode: ThemeMode
  preset: string
  generationBase: ThemeGenerationBase
  recipe: PaletteRecipeId
  customRecipe: string
  contrast: ThemeContrast
  overrides: ThemeTokens
}

export const DEFAULT_THEME_SETTINGS: ThemeSettings = {
  mode: 'preset',
  preset: 'default',
  generationBase: 'canvas-and-ink',
  recipe: 'ink-wash',
  customRecipe: DEFAULT_CUSTOM_RECIPE,
  contrast: 'standard',
  overrides: {},
}

export interface ThemePreset {
  id: string
  name: string
  /** The page this theme is designed around. Choosing the theme moves the
   * page to these colors, unless the writer has set their own. */
  page: { bg: string; ink: string }
  tokens: ThemeTokens
}

/** Saved settings from before the presets were renamed. */
export const LEGACY_PRESET_IDS: Record<string, string> = {
  dark: 'default',
  light: 'slate',
  night: 'slate',
  'high-contrast': 'cobalt',
}

export const THEME_PRESETS: ThemePreset[] = [
  {
    id: 'default',
    name: 'Default',
    page: { bg: '#f0e3c4', ink: '#2b2115' },
    tokens: {
      // Brand — primary accent (buttons, links, active states)
      '--accent': '#ff6b45',
      '--accent-rgb': '255, 107, 69',
      '--accent-link': '#ffab8a',
      '--accent-contrast': '#2b2115',
      '--accent-light': '#ffab8a',
      '--accent-hover': '#ff8a63',
      '--accent-active': '#e65832',

      // Brand — secondary accent (toolbars, "connected" badges, etc.)
      '--secondary': '#1f7a72',
      '--secondary-rgb': '31, 122, 114',
      '--secondary-contrast': '#ffffff',

      // Surfaces — app background and elevated panels
      '--surface-bg': '#123f3a',
      '--surface-raised': '#1a4b45',
      '--surface-border': 'rgba(0, 0, 0, 0.3)',
      '--panel-bg': '#1a4b45',
      '--panel-text': '#f0e3c4',
      '--panel-muted': '#c5baa0',
      '--panel-border': '#91a9a4',
      '--panel-hover-bg': '#315e58',
      '--panel-control-bg': '#2a5751',
      '--panel-accent': '#ffab8a',
      '--panel-accent-rgb': '255, 171, 138',
      '--panel-active-bg': '#f0e3c4',
      '--panel-active-text': '#123f3a',
      '--control-bg': '#1a4b45',
      '--control-text': '#f0e3c4',
      '--control-border': '#8ba7a0',
      '--color-scheme': 'dark',

      // Text
      '--text-bright': '#f0e3c4',
      '--text-bright-rgb': '240, 227, 196',
      '--text-normal': 'rgba(240, 227, 196, 0.85)',
      '--text-muted': 'rgba(240, 227, 196, 0.65)',
      '--text-faint': '#666666',

      // Structure
      '--divider': 'rgba(240, 227, 196, 0.25)',

      // Status
      '--error': '#f87171',
      '--error-light': '#fca5a5',
      '--error-rgb': '248, 113, 113',
      '--success': '#34d399',
      '--success-rgb': '52, 211, 153',
      '--warning': '#fbbf24',
      '--warning-light': '#fef08a',
      '--warning-rgb': '251, 191, 36',

      // Version history diff (added / removed text)
      '--diff-added': '#7ee7d9',
      '--diff-added-rgb': '126, 231, 217',
      '--diff-removed': '#fd8c7e',
      '--diff-removed-rgb': '253, 140, 126',

      // Buttons
      '--button-hover-bg': 'rgba(255, 255, 255, 0.16)',
      '--button-active-bg': 'var(--text-bright)',
      '--button-active-text': 'var(--surface-bg)',

      // Toolbars / bars (top bar, bottom bar, right bar)
      '--toolbar-bg': 'var(--secondary)',
      '--toolbar-border': 'rgba(0, 0, 0, 0.3)',
      '--toolbar-shadow': '0 10px 24px rgba(var(--shadow-rgb), 0.28)',

      // Elevation (drop shadows on modals, dropdowns, toolbars)
      '--shadow-rgb': '20, 16, 10',
      '--surface-shadow': '0 15px 35px rgba(var(--shadow-rgb), 0.45)',

      // Form fields
      '--input-bg': 'rgba(var(--white-rgb), 0.05)',

      // Generic overlay tints (for translucent hover/border effects)
      '--white-rgb': '255, 255, 255',
      '--black-rgb': '0, 0, 0',
    },
  },
  {
    // Slate and sodium: steel-grey chrome around a darker page, lit by a
    // sodium-lamp yellow. The brand color is a gunmetal blue.
    id: 'slate',
    name: 'Dark',
    page: { bg: '#15181b', ink: '#d9dde1' },
    tokens: {
      // Brand — primary accent (buttons, links, active states)
      '--accent': '#f5b942',
      '--accent-rgb': '245, 185, 66',
      '--accent-link': '#f8cf7c',
      '--accent-contrast': '#1c1404',
      '--accent-light': '#f8cf7c',
      '--accent-hover': '#f8c65e',
      '--accent-active': '#dfa22c',

      // Brand — secondary (the settings sidebar, tool stage and badges; Type
      // Guide marks and link underlines are lightened from it until they read)
      '--secondary': '#3d4f63',
      '--secondary-rgb': '61, 79, 99',
      '--secondary-contrast': '#eef2f6',

      // Surfaces — app background and elevated panels
      '--surface-bg': '#1d2126',
      '--surface-raised': '#252a30',
      '--surface-border': '#363d45',
      '--panel-bg': '#22272d',
      '--panel-text': '#e3e6e9',
      '--panel-muted': '#949ca5',
      '--panel-border': '#363d45',
      '--panel-hover-bg': '#2c3238',
      '--panel-control-bg': '#2a3036',
      '--panel-accent': '#f5b942',
      '--panel-accent-rgb': '245, 185, 66',
      '--panel-active-bg': '#f5b942',
      '--panel-active-text': '#1c1404',
      '--control-bg': '#252a30',
      '--control-text': '#e3e6e9',
      '--control-border': '#6b747e',
      '--color-scheme': 'dark',

      // Text
      '--text-bright': '#e3e6e9',
      '--text-bright-rgb': '227, 230, 233',
      '--text-normal': 'rgba(227, 230, 233, 0.88)',
      '--text-muted': 'rgba(227, 230, 233, 0.66)',
      '--text-faint': '#5f6770',

      // Structure
      '--divider': 'rgba(227, 230, 233, 0.16)',

      // Status
      '--error': '#f29488',
      '--error-light': '#f6b4ab',
      '--error-rgb': '242, 148, 136',
      '--success': '#8fd0a0',
      '--success-rgb': '143, 208, 160',
      '--warning': '#f5b942',
      '--warning-light': '#f8d58a',
      '--warning-rgb': '245, 185, 66',

      // Version history diff (added / removed text)
      '--diff-added': '#8cc4f0',
      '--diff-added-rgb': '140, 196, 240',
      '--diff-removed': '#f29488',
      '--diff-removed-rgb': '242, 148, 136',

      // Buttons: an active key is lit sodium yellow
      '--button-hover-bg': 'rgba(255, 255, 255, 0.08)',
      '--button-active-bg': 'var(--accent)',
      '--button-active-text': 'var(--accent-contrast)',

      // Toolbars / bars: steel, a step up from the surface
      '--toolbar-bg': '#2b3138',
      '--toolbar-border': '#111417',
      '--toolbar-shadow': '0 10px 24px rgba(var(--shadow-rgb), 0.45)',

      // Elevation (drop shadows on modals, dropdowns, toolbars)
      '--shadow-rgb': '0, 0, 0',
      '--surface-shadow': '0 15px 35px rgba(var(--shadow-rgb), 0.55)',

      // Form fields
      '--input-bg': 'rgba(var(--white-rgb), 0.05)',

      // Generic overlay tints (for translucent hover/border effects)
      '--white-rgb': '255, 255, 255',
      '--black-rgb': '0, 0, 0',
    },
  },
  {
    // Pure black and white, no hue at all. Anything pressed or current turns
    // solid black. Every pairing of text and the color behind it reaches 7:1.
    id: 'monochrome',
    name: 'Monochrome',
    page: { bg: '#ffffff', ink: '#000000' },
    tokens: {
      // Brand — primary accent (buttons, links, active states)
      '--accent': '#000000',
      '--accent-rgb': '0, 0, 0',
      '--accent-link': '#000000',
      '--accent-contrast': '#ffffff',
      '--accent-light': '#000000',
      '--accent-hover': '#2e2e2e',
      '--accent-active': '#000000',

      // Brand — secondary (the settings sidebar, tool stage, badges, marks)
      '--secondary': '#000000',
      '--secondary-rgb': '0, 0, 0',
      '--secondary-contrast': '#ffffff',

      // Surfaces — app background and elevated panels
      '--surface-bg': '#ffffff',
      '--surface-raised': '#ffffff',
      '--surface-border': '#000000',
      '--panel-bg': '#ffffff',
      '--panel-text': '#000000',
      '--panel-muted': '#3d3d3d',
      '--panel-border': '#000000',
      '--panel-hover-bg': '#ebebeb',
      '--panel-control-bg': '#ffffff',
      '--panel-accent': '#000000',
      '--panel-accent-rgb': '0, 0, 0',
      '--panel-active-bg': '#000000',
      '--panel-active-text': '#ffffff',
      '--control-bg': '#ffffff',
      '--control-text': '#000000',
      '--control-border': '#000000',
      '--color-scheme': 'light',

      // Text
      '--text-bright': '#000000',
      '--text-bright-rgb': '0, 0, 0',
      '--text-normal': '#111111',
      '--text-muted': '#3d3d3d',
      '--text-faint': '#5c5c5c',

      // Structure
      '--divider': 'rgba(0, 0, 0, 0.55)',

      // Status
      '--error': '#a30000',
      '--error-light': '#a30000',
      '--error-rgb': '163, 0, 0',
      '--success': '#0b6b2b',
      '--success-rgb': '11, 107, 43',
      '--warning': '#7a4a00',
      '--warning-light': '#7a4a00',
      '--warning-rgb': '122, 74, 0',

      // Version history diff (added / removed text)
      '--diff-added': '#0b6b2b',
      '--diff-added-rgb': '11, 107, 43',
      '--diff-removed': '#a30000',
      '--diff-removed-rgb': '163, 0, 0',

      // Buttons: an active key is solid black
      '--button-hover-bg': 'rgba(0, 0, 0, 0.1)',
      '--button-active-bg': '#000000',
      '--button-active-text': '#ffffff',

      // Toolbars / bars: white, outlined in black
      '--toolbar-bg': '#ffffff',
      '--toolbar-border': '#000000',
      '--toolbar-shadow': '0 8px 20px rgba(var(--shadow-rgb), 0.22)',

      // Elevation (drop shadows on modals, dropdowns, toolbars)
      '--shadow-rgb': '0, 0, 0',
      '--surface-shadow': '0 12px 30px rgba(var(--shadow-rgb), 0.25)',

      // Form fields
      '--input-bg': '#ffffff',

      // Generic overlay tints (for translucent hover/border effects)
      '--white-rgb': '255, 255, 255',
      '--black-rgb': '0, 0, 0',
    },
  },
  {
    // The daytime twin of Dark: pale steel chrome, a white page, and the same
    // sodium yellow, used only as a fill behind dark text (pressed keys, the
    // current page). Text-colored accents are a deep amber so they read.
    // Every pairing of text and the color behind it reaches 7:1.
    id: 'sodium-day',
    name: 'Sodium Day',
    page: { bg: '#ffffff', ink: '#0e1114' },
    tokens: {
      // Brand — primary accent (buttons, links, active states)
      '--accent': '#6b3d00',
      '--accent-rgb': '107, 61, 0',
      '--accent-link': '#6b3d00',
      '--accent-contrast': '#ffffff',
      '--accent-light': '#6b3d00',
      '--accent-hover': '#5a3300',
      '--accent-active': '#4a2a00',

      // Brand — secondary (the settings sidebar, tool stage, badges, marks)
      '--secondary': '#2b3540',
      '--secondary-rgb': '43, 53, 64',
      '--secondary-contrast': '#ffffff',

      // Surfaces — app background and elevated panels
      '--surface-bg': '#e9ecef',
      '--surface-raised': '#f6f7f8',
      '--surface-border': '#1d2126',
      '--panel-bg': '#f1f3f5',
      '--panel-text': '#0e1114',
      '--panel-muted': '#3c434b',
      '--panel-border': '#1d2126',
      '--panel-hover-bg': '#dfe3e7',
      '--panel-control-bg': '#ffffff',
      '--panel-accent': '#6b3d00',
      '--panel-accent-rgb': '107, 61, 0',
      '--panel-active-bg': '#f5b942',
      '--panel-active-text': '#0e1114',
      '--control-bg': '#ffffff',
      '--control-text': '#0e1114',
      '--control-border': '#1d2126',
      '--color-scheme': 'light',

      // Text
      '--text-bright': '#0e1114',
      '--text-bright-rgb': '14, 17, 20',
      '--text-normal': '#161a1e',
      '--text-muted': '#3c434b',
      '--text-faint': '#5d656e',

      // Structure
      '--divider': 'rgba(14, 17, 20, 0.5)',

      // Status
      '--error': '#a3160c',
      '--error-light': '#a3160c',
      '--error-rgb': '163, 22, 12',
      '--success': '#14652c',
      '--success-rgb': '20, 101, 44',
      '--warning': '#6b3d00',
      '--warning-light': '#6b3d00',
      '--warning-rgb': '107, 61, 0',

      // Version history diff (added / removed text)
      '--diff-added': '#0b4f8a',
      '--diff-added-rgb': '11, 79, 138',
      '--diff-removed': '#a3160c',
      '--diff-removed-rgb': '163, 22, 12',

      // Buttons: an active key is lit sodium yellow
      '--button-hover-bg': 'rgba(14, 17, 20, 0.09)',
      '--button-active-bg': '#f5b942',
      '--button-active-text': '#0e1114',

      // Toolbars / bars: pale steel, outlined in near-black
      '--toolbar-bg': '#f6f7f8',
      '--toolbar-border': '#1d2126',
      '--toolbar-shadow': '0 8px 20px rgba(var(--shadow-rgb), 0.2)',

      // Elevation (drop shadows on modals, dropdowns, toolbars)
      '--shadow-rgb': '14, 17, 20',
      '--surface-shadow': '0 12px 30px rgba(var(--shadow-rgb), 0.24)',

      // Form fields
      '--input-bg': '#ffffff',

      // Generic overlay tints (for translucent hover/border effects)
      '--white-rgb': '255, 255, 255',
      '--black-rgb': '0, 0, 0',
    },
  },
  {
    // Road-sign clarity: white and black, with cobalt as the one color for
    // everything pressed, current or clickable. Every pairing of text and the
    // color behind it reaches 7:1.
    id: 'cobalt',
    name: 'Cobalt Signal',
    page: { bg: '#ffffff', ink: '#000000' },
    tokens: {
      // Brand — primary accent (buttons, links, active states)
      '--accent': '#0039a6',
      '--accent-rgb': '0, 57, 166',
      '--accent-link': '#0039a6',
      '--accent-contrast': '#ffffff',
      '--accent-light': '#0039a6',
      '--accent-hover': '#002f8a',
      '--accent-active': '#00256e',

      // Brand — secondary (the settings sidebar, tool stage, badges, marks)
      '--secondary': '#0039a6',
      '--secondary-rgb': '0, 57, 166',
      '--secondary-contrast': '#ffffff',

      // Surfaces — app background and elevated panels
      '--surface-bg': '#ffffff',
      '--surface-raised': '#ffffff',
      '--surface-border': '#000000',
      '--panel-bg': '#f4f6fb',
      '--panel-text': '#000000',
      '--panel-muted': '#33394a',
      '--panel-border': '#000000',
      '--panel-hover-bg': '#e3e9f6',
      '--panel-control-bg': '#ffffff',
      '--panel-accent': '#0039a6',
      '--panel-accent-rgb': '0, 57, 166',
      '--panel-active-bg': '#0039a6',
      '--panel-active-text': '#ffffff',
      '--control-bg': '#ffffff',
      '--control-text': '#000000',
      '--control-border': '#000000',
      '--color-scheme': 'light',

      // Text
      '--text-bright': '#000000',
      '--text-bright-rgb': '0, 0, 0',
      '--text-normal': '#111111',
      '--text-muted': '#33394a',
      '--text-faint': '#5a6072',

      // Structure
      '--divider': 'rgba(0, 0, 0, 0.55)',

      // Status
      '--error': '#a30000',
      '--error-light': '#a30000',
      '--error-rgb': '163, 0, 0',
      '--success': '#0b6b2b',
      '--success-rgb': '11, 107, 43',
      '--warning': '#7a4a00',
      '--warning-light': '#7a4a00',
      '--warning-rgb': '122, 74, 0',

      // Version history diff (added / removed text)
      '--diff-added': '#0039a6',
      '--diff-added-rgb': '0, 57, 166',
      '--diff-removed': '#a30000',
      '--diff-removed-rgb': '163, 0, 0',

      // Buttons: an active key is solid cobalt
      '--button-hover-bg': 'rgba(0, 57, 166, 0.1)',
      '--button-active-bg': '#0039a6',
      '--button-active-text': '#ffffff',

      // Toolbars / bars: white, outlined in black
      '--toolbar-bg': '#ffffff',
      '--toolbar-border': '#000000',
      '--toolbar-shadow': '0 8px 20px rgba(var(--shadow-rgb), 0.22)',

      // Elevation (drop shadows on modals, dropdowns, toolbars)
      '--shadow-rgb': '0, 0, 0',
      '--surface-shadow': '0 12px 30px rgba(var(--shadow-rgb), 0.25)',

      // Form fields
      '--input-bg': '#ffffff',

      // Generic overlay tints (for translucent hover/border effects)
      '--white-rgb': '255, 255, 255',
      '--black-rgb': '0, 0, 0',
    },
  },
]
